import Uppy from '@uppy/core';
import Dashboard, { DashboardOptions } from '@uppy/dashboard';
import Dropbox from '@uppy/dropbox';
import Transloadit from '@uppy/transloadit';
import { FunctionComponent, useEffect, useMemo, useRef } from 'react';

import { fetchJson } from '@api/ApiHelper';
import { sendAction } from '@helpers/datadog-rum';
import { TransloaditTemplateMap } from '@helpers/uppy';
import { type UppyUploaderProps } from '@components/common/uppy/types/UppyTypes';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';

import './styles/uppy_uploader.scss';

interface TransloaditCreds { params: string; signature: string }

export const UppyUploader: FunctionComponent<UppyUploaderProps> = ({
  handleUpload,
  uniqIdentifier,
  button = false,
  closeOnComplete = false,
  locale = {
    strings: {
      dropPaste: 'Drag file(s) here or %{browse}',
      dropHereOr: 'Drag file(s) here or %{browse}',
      browse: 'click to browse'
    }
  },
  restrictions = {},
  template = 'file_ingest',
  trigger = undefined,
  uploadLinkKey
}) => {
  // useMemo to maintain single instance of Uppy per component lifecycle
  const uppy = useMemo(() => new Uppy({
    allowMultipleUploadBatches: true,
    id: `${uniqIdentifier}-uppy`,
    restrictions: { maxNumberOfFiles: 3000, minFileSize: 1, ...restrictions },
    locale: {
      ...locale,
      strings: {
        ...locale?.strings,
        // Shown when a zero-byte file is added (minFileSize: 1).
        inferiorSize: 'Cannot upload empty file',
      }
    },
  }), [locale, restrictions, uniqIdentifier]);

  const templateName = template.replace(/-/g, '_');
  const dashboardOptions: DashboardOptions = {
    browserBackButtonClose: button,
    closeAfterFinish: button,
    closeModalOnClickOutside: button,
    inline: false,
    proudlyDisplayPoweredByUppy: false,
    showProgressDetails: true,
    trigger,
    width: 10
  };

  const handleError = (file, error): void => {
    sendAction('Error Uploading File to Transloadit', {
      tags: {},
      extra: { file, error }
    });
  };

  const templateId = TransloaditTemplateMap()[templateName]
    || TransloaditTemplateMap().file_ingest;

  const credsRef = useRef<Promise<TransloaditCreds> | null>(null);
  const getCreds = (): Promise<TransloaditCreds> => {
    if (!credsRef.current) {
      credsRef.current = fetchJson<{ data: TransloaditCreds }>({
        url: '/api/v4/private/transloadit_creds',
        method: 'POST',
        body: { template_id: templateId, ...uploadLinkKey && { upload_key: uploadLinkKey } }
      }).then((response) => response.data).catch((error) => {
        credsRef.current = null;
        throw error;
      });
    }
    return credsRef.current;
  };

  useEffect(() => {
    uppy.use(Transloadit, {
      getAssemblyOptions: async (): Promise<any> => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const { params, signature } = await getCreds();
        return { params, signature };
      },
      waitForEncoding: true // ensures request has processed with metadata
    }).on('upload-error', (file, body) => {
      handleError(file, body);
    }).on('transloadit:complete', (assembly) => {
      const files = assembly.results[':original']
        || assembly.results[templateName];
      handleUpload(files);
    }).on('complete', () => {
      credsRef.current = null;
      if (closeOnComplete) {
        uppy.close();
      } else {
        uppy.reset();
      }
    }).use(Dashboard, dashboardOptions).use(Dropbox, {
      companionUrl: Transloadit.COMPANION,
      companionAllowedHosts: Transloadit.COMPANION_PATTERN,
      target: Dashboard
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dashboard: Dashboard = uppy.getPlugin('Dashboard');
  if (dashboard) {
    dashboard.render({});
  }

  return null;
};
